/**
 * Merged orthogonal outlines for a boolean cell grid (one unit square per cell).
 * Boundary edges are grouped by edge-connectivity; each group is traced into a closed SVG path.
 */

export type Point = [number, number];
export type Segment = [Point, Point];

function key(p: Point): string {
    return `${p[0]},${p[1]}`;
}

function parseKey(k: string): Point {
    const [a, b] = k.split(",").map(Number);
    return [a, b];
}

function segmentKey(a: Point, b: Point): string {
    const [p, q] =
        a[0] < b[0] || (a[0] === b[0] && a[1] <= b[1]) ? [a, b] : [b, a];
    return `${p[0]},${p[1]}|${q[0]},${q[1]}`;
}

/** Collect unit-length edges between a filled cell and a non-filled cell. */
export function collectBoundarySegments(
    cols: number,
    rows: number,
    filled: boolean[][]
): Segment[] {
    const isF = (c: number, r: number): boolean =>
        c >= 0 && c < cols && r >= 0 && r < rows && filled[r][c];

    const segs: Segment[] = [];

    for (let r = 0; r <= rows; r++) {
        for (let c = 0; c < cols; c++) {
            if (isF(c, r - 1) !== isF(c, r)) {
                segs.push([
                    [c, r],
                    [c + 1, r],
                ]);
            }
        }
    }

    for (let c = 0; c <= cols; c++) {
        for (let r = 0; r < rows; r++) {
            if (isF(c - 1, r) !== isF(c, r)) {
                segs.push([
                    [c, r],
                    [c, r + 1],
                ]);
            }
        }
    }

    return segs;
}

/** Group segments that share at least one vertex (one closed curve per blob / hole). */
function groupSegmentsByEdgeConnectivity(segs: Segment[]): Segment[][] {
    const segAtVertex = new Map<string, Segment[]>();
    for (const s of segs) {
        for (const p of [s[0], s[1]]) {
            const k = key(p);
            if (!segAtVertex.has(k)) segAtVertex.set(k, []);
            segAtVertex.get(k)!.push(s);
        }
    }

    const seenSeg = new Set<string>();
    const groups: Segment[][] = [];

    for (const s of segs) {
        const sk = segmentKey(s[0], s[1]);
        if (seenSeg.has(sk)) continue;

        const group: Segment[] = [];
        const stack: Segment[] = [s];

        while (stack.length > 0) {
            const e = stack.pop()!;
            const ek = segmentKey(e[0], e[1]);
            if (seenSeg.has(ek)) continue;
            seenSeg.add(ek);
            group.push(e);

            for (const p of [e[0], e[1]]) {
                for (const e2 of segAtVertex.get(key(p)) ?? []) {
                    const e2k = segmentKey(e2[0], e2[1]);
                    if (!seenSeg.has(e2k)) stack.push(e2);
                }
            }
        }

        groups.push(group);
    }

    return groups;
}

function buildAdjacency(segs: Segment[]): Map<string, Point[]> {
    const adj = new Map<string, Point[]>();
    const add = (a: Point, b: Point) => {
        const ka = key(a);
        const kb = key(b);
        if (!adj.has(ka)) adj.set(ka, []);
        if (!adj.has(kb)) adj.set(kb, []);
        adj.get(ka)!.push(b);
        adj.get(kb)!.push(a);
    };
    for (const [a, b] of segs) {
        add(a, b);
    }
    return adj;
}

/** Trace one closed orthogonal loop; returns points without duplicate closing vertex. */
function traceLoop(segs: Segment[]): Point[] {
    const adj = buildAdjacency(segs);
    const keys = [...adj.keys()].sort((a, b) => {
        const [ax, ay] = parseKey(a);
        const [bx, by] = parseKey(b);
        return ax !== bx ? ax - bx : ay - by;
    });
    if (keys.length === 0) return [];

    const startStr = keys[0];
    const start = parseKey(startStr);
    const neighbors = adj.get(startStr);
    if (!neighbors || neighbors.length < 2) return [];

    const firstNei = neighbors[0];
    const path: Point[] = [start, firstNei];
    let prev = start;
    let curr: Point = firstNei;
    const maxIter = Math.max(64, segs.length * 8 + 32);
    let iter = 0;

    while (iter++ < maxIter) {
        const neigh = adj.get(key(curr));
        if (!neigh) return [];
        const next = neigh.find(
            (n) => n[0] !== prev[0] || n[1] !== prev[1]
        );
        if (!next) return [];
        if (next[0] === start[0] && next[1] === start[1]) {
            break;
        }
        path.push(next);
        prev = curr;
        curr = next;
    }

    if (iter >= maxIter) {
        return [];
    }

    return path;
}

function pointsToPathD(points: Point[]): string {
    if (points.length < 2) return "";
    const [x0, y0] = points[0];
    const rest = points.slice(1);
    const body = rest.map(([x, y]) => `L${x} ${y}`).join("");
    return `M${x0} ${y0}${body}Z`;
}

/** SVG path `d` strings for each closed boundary (outer + holes as separate paths). */
export function mergedBlobOutlinePathDs(
    cols: number,
    rows: number,
    filled: boolean[][]
): string[] {
    const segs = collectBoundarySegments(cols, rows, filled);
    if (segs.length === 0) return [];

    const groups = groupSegmentsByEdgeConnectivity(segs);
    const out: string[] = [];

    for (const g of groups) {
        const pts = traceLoop(g);
        const d = pointsToPathD(pts);
        if (d) out.push(d);
    }

    return out;
}
